import { EyeOutlined, FileTextOutlined, CalendarOutlined, DownloadOutlined, ReloadOutlined, CheckOutlined, BankOutlined, FilePdfOutlined, FileExcelOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { ConfigProvider, DatePicker, Select, Card, Table, Tag, Space, Button, Modal, Tabs, Statistic, Row, Col, Spin, Popconfirm, Alert, Segmented, Tooltip, Input } from 'antd';
import * as XLSX from 'xlsx';
import frFR from "antd/locale/fr_FR";
import { useEffect, useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import axiosInstance from '../../../variables/api/axiosInstance';
import { frenchMonth, frenchDateOnly, formatJustificatifCheque } from '../../../variables/functions';
import { displayNumbers } from '../../../variables/numbersFunctions';
import BordereauEmissions from './bordereaux/BordereauEmissions';
import BordereauAnnulations from './bordereaux/BordereauAnnulations';
import BordereauSinistresPayes from './bordereaux/BordereauSinistresPayes';
import BordereauRecoursEncaisses from './bordereaux/BordereauRecoursEncaisses';
import CompteDeCession from './bordereaux/CompteDeCession';
import EtatChequesEmis from './documents/EtatChequesEmis';
import { LOGGED_USER } from '../../../variables/constants';
import { useNavigate } from 'react-router-dom';
import ChequeAssignmentModal from './modals/ChequeAssignementModal';

// Taux d'indemnité de gestion (15 % de la prime nette) — même définition que le Compte de
// cession (Cession.js) et le document officiel.
const TAUX_INDEMNITE = 0.15;

/**
 * Indemnité de gestion d'une composante : la valeur STOCKÉE si elle est renseignée, sinon
 * le repli 15 % sur la base. L'endpoint de liste (/api/cession/validees → CessionResponseDTO)
 * peut renvoyer des indemnités nulles ; sans ce repli, elles disparaissent du solde.
 */
const indemniteDe = (valeurStockee, base) =>
    (valeurStockee && valeurStockee > 0) ? valeurStockee : (base || 0) * TAUX_INDEMNITE;

/**
 * SOLDE d'une (sous-)cession = débit − crédit, calculé À L'IDENTIQUE du Compte de cession.
 * solde < 0 ⇒ en faveur du Pool (la compagnie doit verser au Pool).
 *
 * Auparavant recalculé à 3 endroits avec `indemnitesEmissions || 0` (sans repli) : quand
 * les indemnités arrivaient nulles, le solde tombait à ≈ −totalEmissions, d'où un montant
 * de chèque proposé égal au MONTANT TOTAL des émissions au lieu du solde net.
 */
const calculerSoldeCession = (c) => {
    if (!c) return 0;
    const indemnEmissions = indemniteDe(c.indemnitesEmissions, c.totalEmissions);
    const indemnAnnulations = indemniteDe(c.indemnitesAnnulations, c.totalAnnulations);
    const debit = (c.totalAnnulations || 0) + indemnEmissions + (c.totalSinistres || 0);
    const credit = (c.totalEmissions || 0) + indemnAnnulations + (c.totalRecours || 0);
    return debit - credit;
};

// Formate une date en français (JJ/MM/AAAA). Robuste aux valeurs nulles / 'N/A' / invalides
// renvoyées par les fonctions SQL des bordereaux.
const fmtDateFr = (v) => {
    if (!v || v === 'N/A') return '-';
    const d = new Date(v);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('fr-FR');
};

/**
 * Décompose le montant cédé d'un item de bordereau (émissions/annulations de la modale
 * « Voir les bordereaux ») en PN Pool / PN Compagnie / accessoires / taxes.
 *
 * Même logique que la fonction SQL get_contrats_reglements_cession : proratisation sur
 * primeACeder / contrat.primeTotale (les 4 parts somment EXACTEMENT à primeACeder). Ici le
 * contrat complet est déjà embarqué dans l'item (EmissionsDTO.contrat), donc pas besoin
 * d'appel serveur. primeTotale nulle ⇒ tout en PN Pool.
 */
const decomposerItemCession = (it) => {
    const ttc = Number(it?.primeACeder) || 0;
    // 1) Décomposition À PLAT déjà fournie (items enrichis depuis get_contrats_reglements_cession) :
    //    on l'utilise directement — c'est la source fiable (les bordereaux agrégés ne portent que le
    //    montant TTC via `primeACeder`, sans PN/accessoires/taxes, d'où PN Pool = TTC auparavant).
    if (it && (it.primeNettePool != null || it.primeNetteCompagnie != null
        || it.accessoires != null || it.taxes != null)) {
        const pnc = Number(it.primeNetteCompagnie) || 0;
        const acc = Number(it.accessoires) || 0;
        const tax = Number(it.taxes) || 0;
        const pnp = it.primeNettePool != null ? Number(it.primeNettePool) : (ttc - pnc - acc - tax);
        return { ...it, primeNettePool: pnp, primeNetteCompagnie: pnc, accessoires: acc, taxes: tax, primeTTC: ttc };
    }
    // 2) Repli : décomposition depuis le contrat embarqué (prorata), sinon tout en PN Pool.
    const c = it?.contrat || {};
    const pt = Number(c.primeTotale) || 0;
    // prime_totale connue ⇒ prorata ; sinon (0 en base, anomalie fréquente) ratio = 1 : on
    // prend les accessoires/taxes/part compagnie DIRECTS du contrat (le règlement = prime pleine).
    // Sans ce repli, un primeTotale à 0 renvoyait accessoires = taxes = 0 et tout en PN Pool.
    const ratio = pt !== 0 ? ttc / pt : 1;
    const pnc = (Number(c.primeNetteCompagnie) || 0) * ratio;
    // Contrat fractionné : accessoires/taxes de la FRACTION (homogènes à primeTotale), sinon annuels.
    const acc = (Number(c.accessoiresFractionnee) || Number(c.accessoires) || 0) * ratio;
    const tax = (Number(c.taxesFractionnee) || Number(c.taxes) || 0) * ratio;
    return {
        ...it,
        primeNettePool: ttc - pnc - acc - tax, // par soustraction ⇒ somme exacte = primeACeder
        primeNetteCompagnie: pnc,
        accessoires: acc,
        taxes: tax,
        primeTTC: ttc,
    };
};
const enrichirItemsCession = (items) => (items || []).map(decomposerItemCession);

/**
 * Injecte sur chaque item de bordereau la décomposition prime nette (PN Pool/Compagnie, accessoires,
 * taxes) issue du flux plat get_contrats_reglements_cession, appariée par code_id de règlement
 * (repli sur police|montant). Nécessaire car les bordereaux agrégés (fonctions SQL *_agg_with_bordereaux)
 * ne renvoient que le montant TTC (`primeACeder`), sans décomposition.
 */
const appliquerDecomposition = (items, map) => (items || []).map((it) => {
    const parts = map[`id:${it?.codeId}`]
        || map[`np:${it?.numeroPolice}|${Number(it?.primeACeder) || 0}`];
    return parts ? { ...it, ...parts } : it;
});

// Libellé de catégorie affiché : les codes TPV (A/B/T) sont regroupés sous « TPV » (comme les
// fonctions SQL des cessions/états) ; les autres codes (M…) restent inchangés.
const labelCategorie = (code) => (["A", "B", "T"].includes(code) ? "TPV" : (code || "—"));

// Regroupe les sous-cessions par LIBELLÉ de catégorie affiché : A/B/T fusionnées en une seule
// « TPV » (totaux sommés), les autres inchangées. `_membres` garde les cessions sous-jacentes pour
// aller chercher/concaténer leurs bordereaux au clic. N'affecte QUE le modal Détails (pas les
// sous-cessions de la ligne compagnie qui pilotent l'assignation de chèque, restées par cession).
const fusionnerSousCessionsParLibelle = (sousCessions) => {
    const groupes = new Map();
    (sousCessions || []).forEach((sc) => {
        const label = labelCategorie(sc?.categorie?.codeCategorie ?? sc?.categorieCode);
        if (!groupes.has(label)) {
            groupes.set(label, {
                ...sc,
                categorie: { ...(sc?.categorie || {}), codeCategorie: label, nom: label },
                categorieCode: label,
                totalEmissions: 0, totalAnnulations: 0, totalSinistres: 0, totalRecours: 0,
                _membres: [],
            });
        }
        const g = groupes.get(label);
        g.totalEmissions += sc.totalEmissions || 0;
        g.totalAnnulations += sc.totalAnnulations || 0;
        g.totalSinistres += sc.totalSinistres || 0;
        g.totalRecours += sc.totalRecours || 0;
        g._membres.push(sc);
    });
    return Array.from(groupes.values());
};

const CessionsPool = () => {
    const [compagnies, setCompagnies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cessionsGroupees, setCessionsGroupees] = useState([]);
    // Période : vide = TOUS LES MOIS (par défaut). La liste utilise désormais la voie LÉGÈRE
    // (totaux persistés, sans agrégation de bordereaux) → charger tout l'historique reste rapide.
    // Le choix de l'utilisateur est persisté (sticky) mais le défaut est « tous les mois ».
    const [selectedPeriode, setSelectedPeriode] = useState(
        () => sessionStorage.getItem("cessionsPool_periode") || null);
    const [selectedCompagnie, setSelectedCompagnie] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedCessionGroup, setSelectedCessionGroup] = useState(null);
    // État de correction Pool (anomalie) de la cession active + bascule d'affichage compagnie/corrigé.
    const [correctionCessionActive, setCorrectionCessionActive] = useState(null);
    // Défaut = CORRIGÉ (comme la rétrocession, qui utilise toujours la version corrigée si elle existe).
    // Le toggle « compagnie » permet de revenir à la version originale. Sans correction, les deux sont égaux.
    const [voirCorrigeCession, setVoirCorrigeCession] = useState(true);
    // Lignes anomales (contrat sans date) détectées côté backend + verrou d'action.
    const [anomaliesCessionActive, setAnomaliesCessionActive] = useState([]);
    const [savingCorrection, setSavingCorrection] = useState(false);
    // Habilitation HOP112 : corriger un compte de cession pour anomalie.
    const hasHOP112 = (LOGGED_USER?.profil?.habilitations || []).some((h) => h?.codeHabilitation === "HOP112");
    // Habilitation HOP119 : annuler une cession non rétrocédée (réservé au Pool).
    const hasHOP119 = (LOGGED_USER?.profil?.habilitations || []).some((h) => h?.codeHabilitation === "HOP119");
    // Annulation de cession : cession ciblée + motif obligatoire.
    const [annulCession, setAnnulCession] = useState(null);
    const [annulMotif, setAnnulMotif] = useState("");
    const [annulLoading, setAnnulLoading] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [cessionDetailsCache, setCessionDetailsCache] = useState({});
    const [cessions, setCessions] = useState([]);
    const [chequeModalVisible, setChequeModalVisible] = useState(false);
    const [selectedCessionForCheque, setSelectedCessionForCheque] = useState(null);
    const [banques, setBanques] = useState([]);
    const [loadingValidation, setLoadingValidation] = useState({});

    // État chèques (impression / export par mois)
    const [chequesEtatModalVisible, setChequesEtatModalVisible] = useState(false);
    const [selectedMoisForChequesEtat, setSelectedMoisForChequesEtat] = useState(null);
    const [isPrintingCheques, setIsPrintingCheques] = useState(false);

    const navigate = useNavigate();

    // Refs pour l'impression
    const printEmissionsRef = useRef();
    const printAnnulationsRef = useRef();
    const printSinistresRef = useRef();
    const printRecoursRef = useRef();
    const printCessionRef = useRef();
    const printChequesEtatRef = useRef();

    // Nom de fichier PDF (sinon le PDF hérite du titre d'onglet « PoolIns ») : compagnie +
    // catégorie + mois de la cession sélectionnée, aligné sur l'export Excel.
    const cessionActive = selectedCessionGroup?.cessionActive;

    // Récupère l'état de correction Pool (anomalie) + les lignes anomales de la cession active.
    useEffect(() => {
        console.log("Cession active changée, rechargement correction/anomalies :", cessionActive);
        const codeId = cessionActive?.codeId;
        setVoirCorrigeCession(false);
        setCorrectionCessionActive(null);
        setAnomaliesCessionActive([]);
        if (!codeId) return;
        axiosInstance.get(`/api/cession/${codeId}/correction`)
            .then((r) => {
                setCorrectionCessionActive(r.data);
                // Quand une correction existe, le compte AFFICHÉ est par défaut la version CORRIGÉE (Pool).
                if (r.data?.corrige) setVoirCorrigeCession(true);
            })
            .catch(() => setCorrectionCessionActive(null));
        axiosInstance.get(`/api/cession/${codeId}/lignes-anomales`)
            .then((r) => setAnomaliesCessionActive(Array.isArray(r.data) ? r.data : []))
            .catch(() => setAnomaliesCessionActive([]));
    }, [cessionActive?.codeId]);

    // Recharge l'état de correction après une action (corriger / annuler).
    const rechargerCorrectionCession = (codeId) =>
        axiosInstance.get(`/api/cession/${codeId}/correction`)
            .then((r) => setCorrectionCessionActive(r.data))
            .catch(() => { });

    // Corrige le compte de cession en écartant les lignes sur contrat SANS date (HOP112). La version
    // compagnie (originale) est conservée. Prend effet directement dans la rétrocession (soldeCorrige /
    // totalEmissionsCorrige relus par le récapitulatif).
    //   force=true → correction EXCEPTIONNELLE malgré une rétrocession déjà validée (verrou bypassé).
    const corrigerAnomalieCession = (force = false) => {
        const codeId = cessionActive?.codeId;
        if (!codeId) return;
        const total = anomaliesCessionActive.reduce((s, l) => s + (Number(l.primeNettePool) || 0), 0);
        Modal.confirm({
            title: force ? "⚠️ Forcer la correction (rétrocession déjà validée) ?" : "Corriger le compte de cession ?",
            content: force
                ? `À TITRE EXCEPTIONNEL. La rétrocession de cette période est DÉJÀ VALIDÉE. Corriger maintenant écartera ${anomaliesCessionActive.length} ligne(s) (~${displayNumbers(Math.round(total))} F) et modifiera la différence de solde de cession — la rétrocession déjà validée devra être revérifiée/refaite. Continuer ?`
                : `${anomaliesCessionActive.length} ligne(s) sur contrat sans date d'effet/échéance (~${displayNumbers(Math.round(total))} F) seront écartées du compte. La version compagnie (originale) est conservée. ⚠️ Cette correction impacte la différence de solde de cession de la rétrocession.`,
            okText: force ? "Forcer la correction" : "Corriger",
            okButtonProps: force ? { danger: true } : {},
            cancelText: "Annuler",
            onOk: () => {
                setSavingCorrection(true);
                const url = `/api/cession/${codeId}/corriger-anomalie${force ? '?force=true' : ''}`;
                return axiosInstance.put(url)
                    .then(() => rechargerCorrectionCession(codeId))
                    .then(() => { setVoirCorrigeCession(true); toast.success("Compte de cession corrigé."); })
                    .catch(() => toast.error("Échec de la correction du compte de cession."))
                    .finally(() => setSavingCorrection(false));
            },
        });
    };

    // Annule la correction Pool → rétablit la version compagnie (tant que la rétro n'est pas validée).
    const annulerCorrectionCession = () => {
        const codeId = cessionActive?.codeId;
        if (!codeId) return;
        Modal.confirm({
            title: "Annuler la correction ?",
            content: "Le compte de cession reviendra à la version compagnie (originale). Possible uniquement tant que la rétrocession n'est pas validée.",
            okText: "Annuler la correction",
            cancelText: "Retour",
            onOk: () => {
                setSavingCorrection(true);
                return axiosInstance.put(`/api/cession/${codeId}/annuler-correction`)
                    .then(() => rechargerCorrectionCession(codeId))
                    .then(() => { setVoirCorrigeCession(false); toast.success("Correction annulée."); })
                    .catch(() => toast.error("Échec de l'annulation."))
                    .finally(() => setSavingCorrection(false));
            },
        });
    };

    // Le compte de cession est désormais ALIGNÉ sur la rétrocession (calculerCompteCessionRetroAligne) :
    // base persistée corrigée + déduction de la prime reportée. L'ancien totauxAvecCorrection (qui ne
    // connaissait pas la reportée) a été retiré.
    const moisR = cessionActive?.moisRapport || '';
    const moisLabel = moisR ? `${frenchMonth(moisR.split('-')[1])}_${moisR.split('-')[0]}` : '';
    const compagnieTag = cessionActive?.compagnieNom ? `_${String(cessionActive.compagnieNom).replace(/\s+/g, '-')}` : '';
    const catTag = cessionActive?.categorieCode ? `_${cessionActive.categorieCode}` : '';
    const docSuffix = `${compagnieTag}${catTag}${moisLabel ? `_${moisLabel}` : ''}`;

    // Impression sur UNE page sans marge (sinon marges par défaut → débordement 2e page).
    const A4_NO_MARGIN = `
      @page { size: A4 portrait; margin: 0; }
      @media print {
        html, body { width: 210mm; margin: 0 !important; padding: 0 !important;
          -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `;

    // Handlers d'impression
    const handlePrintEmissions = useReactToPrint({
        content: () => printEmissionsRef.current,
        documentTitle: `Bordereau_des_emissions${docSuffix}`,
    });

    const handlePrintAnnulations = useReactToPrint({
        content: () => printAnnulationsRef.current,
        documentTitle: `Bordereau_des_annulations${docSuffix}`,
    });

    const handlePrintSinistres = useReactToPrint({
        content: () => printSinistresRef.current,
        documentTitle: `Bordereau_des_sinistres_payes${docSuffix}`,
    });

    const handlePrintRecours = useReactToPrint({
        content: () => printRecoursRef.current,
        documentTitle: `Bordereau_des_recours_encaisses${docSuffix}`,
    });

    const handlePrintCession = useReactToPrint({
        content: () => printCessionRef.current,
        documentTitle: `Compte_de_cession${docSuffix}`,
        pageStyle: A4_NO_MARGIN,
    });

    const handlePrintChequesEtat = useReactToPrint({
        content: () => printChequesEtatRef.current,
        documentTitle: `Etat_cheques_emis${moisLabel ? `_${moisLabel}` : ''}`,
    });

    /**
     * Construit les lignes de l'état des chèques pour un mois donné.
     * - Une ligne par chèque émis (compagnie + catégorie + N° chèque + banque + montant).
     * - Si une compagnie n'a émis aucun chèque, on ajoute une ligne avec montant = 0.
     */
    const buildChequesRows = (moisGroupe) => {
        if (!moisGroupe?.compagnies) return { rows: [], totalMontant: 0 };

        const rows = [];
        let totalMontant = 0;

        moisGroupe.compagnies.forEach((comp) => {
            const sousAvecCheque = (comp.sousCessions || []).filter((sc) => sc.numeroCheque);

            if (sousAvecCheque.length === 0) {
                rows.push({
                    key: `${comp.key}-no-cheque`,
                    compagnie: comp.compagnie?.raisonSociale || '',
                    categorie: '—',
                    numeroCheque: '—',
                    banque: '—',
                    montantCheque: 0,
                });
            } else {
                sousAvecCheque.forEach((sc) => {
                    const montant = sc.montantCheque || 0;
                    totalMontant += montant;
                    rows.push({
                        key: `${comp.key}-${sc.codeId}`,
                        compagnie: comp.compagnie?.raisonSociale || '',
                        categorie: labelCategorie(sc.categorie?.codeCategorie),
                        numeroCheque: sc.numeroCheque || '—',
                        banque: sc.banque || '—',
                        montantCheque: montant,
                    });
                });
            }
        });

        return { rows, totalMontant };
    };

    const ouvrirEtatChequesModal = (moisGroupe) => {
        setSelectedMoisForChequesEtat(moisGroupe);
        setChequesEtatModalVisible(true);
    };

    const fermerEtatChequesModal = () => {
        setChequesEtatModalVisible(false);
        setSelectedMoisForChequesEtat(null);
    };

    const lancerImpressionChequesEtat = () => {
        setIsPrintingCheques(true);
        setTimeout(() => {
            handlePrintChequesEtat();
            setTimeout(() => setIsPrintingCheques(false), 200);
        }, 150);
    };

    const exporterChequesEtatExcel = (moisGroupe) => {
        if (!moisGroupe) return;
        const { rows, totalMontant } = buildChequesRows(moisGroupe);

        const dataExcel = rows.map((row, idx) => ({
            'N°': idx + 1,
            'Compagnie': row.compagnie,
            'Catégorie': row.categorie,
            'N° Chèque': row.numeroCheque,
            'Banque': row.banque,
            'Montant (F CFA)': row.montantCheque,
        }));

        // Ligne de total
        dataExcel.push({
            'N°': '',
            'Compagnie': '',
            'Catégorie': '',
            'N° Chèque': '',
            'Banque': 'TOTAL',
            'Montant (F CFA)': totalMontant,
        });

        const worksheet = XLSX.utils.json_to_sheet(dataExcel);
        worksheet['!cols'] = [
            { wch: 6 },
            { wch: 35 },
            { wch: 12 },
            { wch: 18 },
            { wch: 25 },
            { wch: 18 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Etat Chèques');

        const [annee, mois] = (moisGroupe.moisRapport || '').split('-');
        const filename = `Etat_Cheques_${frenchMonth(mois) || mois}_${annee}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    useEffect(() => {
        // Charger les catégories et compagnies
        Promise.all([
            axiosInstance.get(`/api/organisme/assurance`),
            axiosInstance.get(`/api/parametre/type/BANQUE`),
        ])
            .then(([compRes, banqueRes]) => {
                setCompagnies(compRes.data);
                console.log("Banques:", banqueRes.data);
                setBanques(banqueRes.data || []);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des données");
            });
        // NB : le chargement des cessions est déclenché par l'effet [selectedPeriode, selectedCompagnie]
        // ci-dessous (qui se joue aussi au montage) → on évite le double appel initial.
    }, []);

    // Persiste la période sélectionnée (sticky) ; vide = tous les mois → on efface la clé.
    useEffect(() => {
        if (selectedPeriode) sessionStorage.setItem("cessionsPool_periode", selectedPeriode);
        else sessionStorage.removeItem("cessionsPool_periode");
    }, [selectedPeriode]);

    useEffect(() => {
        chargerCessionsValidees();
    }, [selectedPeriode, selectedCompagnie]);

    const chargerCessionsValidees = () => {
        setLoading(true);
        const params = {};

        // Mois envoyé UNIQUEMENT si une période est choisie ; sinon → tous les mois (voie légère).
        if (selectedPeriode) {
            params.mois = selectedPeriode;
        }

        if (selectedCompagnie) {
            params.company = selectedCompagnie;
        }

        // Appel à l'endpoint optimisé (version légère)
        axiosInstance
            .get(`/api/cession/validees`, { params })
            .then((res) => {
                console.log("Cessions reçues:", res.data);
                setCessions(res.data);
                // Grouper les cessions par mois/année et compagnie
                const grouped = grouperCessions(res.data);
                setCessionsGroupees(grouped);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des cessions");
                setLoading(false);
            });
    };

    const grouperCessions = (cessions) => {
        // Niveau 1 : groupement par mois (YYYY-MM)
        // Niveau 2 : à l'intérieur de chaque mois, sous-groupement par compagnie
        const moisGroupes = {};

        cessions.forEach((cession) => {
            const moisRapport = cession?.moisRapport?.substring(0, 7); // Format: YYYY-MM
            const compagnieCode = cession?.compagnie?.code;
            const compKey = `${moisRapport}_${compagnieCode}`;

            // Initialiser le groupe mois
            if (!moisGroupes[moisRapport]) {
                moisGroupes[moisRapport] = {
                    key: moisRapport,
                    moisRapport,
                    dateEmission: cession.dateEmission,
                    compagniesMap: {},
                    totalEmissionsGlobal: 0,
                    totalAnnulationsGlobal: 0,
                    totalSinistresGlobal: 0,
                    totalRecoursGlobal: 0,
                    soldeGlobal: 0,
                    nbSousCessions: 0,
                };
            }

            const moisGroupe = moisGroupes[moisRapport];

            // Initialiser le sous-groupe compagnie
            if (!moisGroupe.compagniesMap[compKey]) {
                moisGroupe.compagniesMap[compKey] = {
                    key: compKey,
                    moisRapport,
                    compagnie: cession.compagnie,
                    dateEmission: cession.dateEmission,
                    sousCessions: [],
                    totalEmissionsGlobal: 0,
                    totalAnnulationsGlobal: 0,
                    totalSinistresGlobal: 0,
                    totalRecoursGlobal: 0,
                    soldeGlobal: 0
                };
            }

            const compGroupe = moisGroupe.compagniesMap[compKey];

            // Ajouter la sous-cession à la compagnie
            compGroupe.sousCessions.push(cession);
            compGroupe.totalEmissionsGlobal += cession.totalEmissions || 0;
            compGroupe.totalAnnulationsGlobal += cession.totalAnnulations || 0;
            compGroupe.totalSinistresGlobal += cession.totalSinistres || 0;
            compGroupe.totalRecoursGlobal += cession.totalRecours || 0;

            // Solde unitaire (avec repli 15 % sur les indemnités, cf. calculerSoldeCession) :
            // il alimente les soldes agrégés compagnie/mois, qui doivent concorder avec le
            // solde par ligne et le montant de chèque proposé.
            const soldeUnitaire = calculerSoldeCession(cession);
            compGroupe.soldeGlobal += soldeUnitaire;

            // Mise à jour des totaux du mois
            moisGroupe.totalEmissionsGlobal += cession.totalEmissions || 0;
            moisGroupe.totalAnnulationsGlobal += cession.totalAnnulations || 0;
            moisGroupe.totalSinistresGlobal += cession.totalSinistres || 0;
            moisGroupe.totalRecoursGlobal += cession.totalRecours || 0;
            moisGroupe.soldeGlobal += soldeUnitaire;
            moisGroupe.nbSousCessions += 1;

            // Conserver la date la plus récente pour le mois
            if (new Date(cession.dateEmission) > new Date(moisGroupe.dateEmission)) {
                moisGroupe.dateEmission = cession.dateEmission;
            }
        });

        // Conversion : compagniesMap → tableau trié + tri des mois (plus récent en premier)
        return Object.values(moisGroupes)
            .map((m) => {
                // Ordre des compagnies = ordre du CODE de la compagnie (tri naturel : 2 avant 10).
                const compagnies = Object.values(m.compagniesMap).sort((a, b) =>
                    String(a.compagnie?.code || "").localeCompare(String(b.compagnie?.code || ""), undefined, { numeric: true })
                );
                const { compagniesMap, ...rest } = m;
                return {
                    ...rest,
                    compagnies,
                    nbCompagnies: compagnies.length,
                };
            })
            .sort((a, b) => b.moisRapport.localeCompare(a.moisRapport));
    };

    const chargerDetailsCessionByCategorie = async (cession) => {
        // Vérifier si déjà en cache
        if (cessionDetailsCache[cession.codeId]) {
            return cessionDetailsCache[cession.codeId];
        }
        console.log("Chargement des détails de la cession", cession);
        setLoadingDetails(true);
        try {
            console.log("Chargement des détails de la cession hmmmm", cession);
            // Catégorie GROUPÉE (A/B/T → TPV) : la fonction SQL renvoie alors tous les contrats TPV.
            const categorieGroupee = labelCategorie(cession.categorie.codeCategorie);
            const response = await axiosInstance.get(`/api/cession/validees/details`, {
                params: {
                    mois: cession.moisRapport,
                    compagnie: cession.compagnie.code,
                    categorie: categorieGroupee
                }
            });
            const details = response.data;
            console.log("Details: ", details);

            // Enrichissement de la décomposition prime nette : les bordereaux agrégés ne portent que
            // le montant TTC (primeACeder). On récupère la décomposition par règlement via le flux plat
            // get_contrats_reglements_cession (même source que l'écran /cession) et on l'applique par code_id.
            try {
                const moisParam = `${String(cession.moisRapport).slice(0, 7)}-01`;
                const decompoRes = await axiosInstance.get(
                    `/api/contrat/organisme/month/cession/${cession.compagnie.code}`,
                    { timeout: 120000, params: { codeCategorie: categorieGroupee, mois: moisParam } }
                );
                const map = {};
                (decompoRes.data || []).forEach((contrat) => {
                    (contrat.reglements || []).forEach((reg) => {
                        const parts = {
                            primeNettePool: reg.primeNettePool,
                            primeNetteCompagnie: reg.primeNetteCompagnie,
                            accessoires: reg.accessoires,
                            taxes: reg.taxes,
                        };
                        if (reg.codeId) map[`id:${reg.codeId}`] = parts;
                        if (contrat.numeroPoliceP != null) map[`np:${contrat.numeroPoliceP}|${Number(reg.montant) || 0}`] = parts;
                    });
                });
                if (details?.bordereauEmissions) {
                    details.bordereauEmissions.items = appliquerDecomposition(details.bordereauEmissions.items, map);
                }
                if (details?.bordereauAnnulations) {
                    details.bordereauAnnulations.items = appliquerDecomposition(details.bordereauAnnulations.items, map);
                }
            } catch (e) {
                // Décomposition indisponible → repli sur le contrat embarqué (comportement précédent).
                console.error("Décomposition prime nette indisponible:", e);
            }

            // Mettre en cache
            setCessionDetailsCache(prev => ({
                ...prev,
                [cession.categorieCode]: details
            }));

            setLoadingDetails(false);
            return details;
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des détails");
            setLoadingDetails(false);
            return null;
        }
    };

    const ouvrirDetailsSousCession = async (cession) => {
        // La catégorie envoyée aux fonctions SQL est le LIBELLÉ groupé (TPV pour A/B/T) → les fonctions
        // `get_cessions_validees_by_month_and_categorie_agg_with_bordereaux` et
        // `get_contrats_reglements_cession` renvoient alors TOUS les contrats A+B+T (pas juste A).
        let cessionComplete = cession;
        const dejaCharge = cession?.bordereauEmissions?.items?.length > 0;
        if (!dejaCharge) {
            cessionComplete = await chargerDetailsCessionByCategorie(cession);
        }
        if (!cessionComplete) return;

        // La réponse `/validees/details` (bordereaux) NE porte PAS le code_id de la cession → on le
        // réinjecte depuis la sous-cession d'origine (indispensable pour corriger/annuler l'anomalie).
        setSelectedCessionGroup(prev => ({
            ...prev,
            // On préserve les totaux PERSISTÉS de la sous-cession d'origine (/validees light) : la réponse
            // /validees/details ne porte ni totalEmissionsCorrige ni totalEmissionsReportees, requis pour
            // le compte de cession aligné sur la rétrocession.
            cessionActive: {
                ...cessionComplete,
                codeId: cession?.codeId ?? cessionComplete?.codeId,
                compagnie: cessionComplete?.compagnie ?? cession?.compagnie,
                totalEmissions: cession?.totalEmissions ?? cessionComplete?.totalEmissions,
                totalSinistres: cession?.totalSinistres ?? cessionComplete?.totalSinistres,
                totalEmissionsCorrige: cession?.totalEmissionsCorrige ?? cessionComplete?.totalEmissionsCorrige,
                totalEmissionsReportees: cession?.totalEmissionsReportees ?? cessionComplete?.totalEmissionsReportees,
            }
        }));
        setDetailModalVisible(true);
    };

    // Fonction pour valider un chèque
    const validerCheque = async (cession) => {
        setLoadingValidation(prev => ({ ...prev, [cession.codeId]: true }));

        try {
            await axiosInstance.get(`/api/cession/valider-cheque/${cession.codeId}`);
            toast.success('Chèque validé avec succès');

            // Recharger les cessions pour mettre à jour l'affichage
            chargerCessionsValidees();
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Erreur lors de la validation du chèque');
        } finally {
            setLoadingValidation(prev => ({ ...prev, [cession.codeId]: false }));
        }
    };

    // Annuler une cession NON rétrocédée (Pool, HOP119) : remet ses éléments en non cédé.
    const confirmerAnnulationCession = async () => {
        if (!annulCession) return;
        if (!annulMotif.trim()) { toast.warning("Le motif d'annulation est obligatoire."); return; }
        setAnnulLoading(true);
        try {
            await axiosInstance.put(`/api/cession/${annulCession.codeId}/annuler`, { motif: annulMotif.trim() });
            toast.success("Cession annulée — éléments remis en non cédé");
            setAnnulCession(null);
            setAnnulMotif("");
            chargerCessionsValidees();
        } catch (error) {
            console.error(error);
            // Le backend masque le message des erreurs (include-message=never) → on mappe par CODE HTTP.
            const status = error?.response?.status;
            const data = error?.response?.data;
            let message;
            if (status === 403) {
                message = "Action réservée au Pool avec l'habilitation HOP119.";
            } else if (status === 409) {
                message = "Annulation impossible : la période est déjà rétrocédée ou le chèque est encaissé.";
            } else if (status === 422) {
                message = "Le motif d'annulation est obligatoire.";
            } else {
                message = data?.message || (typeof data === 'string' ? data : null)
                    || error?.message || "Erreur lors de l'annulation de la cession";
            }
            toast.error(message);
        } finally {
            setAnnulLoading(false);
        }
    };

    const colonnesPrincipales = [
        {
            title: 'Période',
            dataIndex: 'moisRapport',
            key: 'moisRapport',
            render: (mois) => {
                const [annee, moisNum] = mois.split('-');
                return (
                    <Space size={12}>
                        <div style={{
                            width: 40, height: 40, borderRadius: 10,
                            background: 'linear-gradient(135deg, #F39C12 0%, #E67E22 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 3px 8px rgba(243, 156, 18, 0.35)'
                        }}>
                            <CalendarOutlined style={{ color: 'white', fontSize: 18 }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 700, fontSize: 15, color: '#2c3e50', lineHeight: 1.2 }}>
                                {frenchMonth(moisNum)}
                            </span>
                            <span style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 500 }}>
                                {annee}
                            </span>
                        </div>
                    </Space>
                );
            },
            width: 200,
            sorter: (a, b) => a.moisRapport.localeCompare(b.moisRapport),
        },
        {
            title: 'Compagnies',
            key: 'nbCompagnies',
            render: (_, record) => (
                <Tag color="geekblue" style={{ fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 8 }}>
                    {record.nbCompagnies} compagnie{record.nbCompagnies > 1 ? 's' : ''}
                </Tag>
            ),
            align: 'center',
            width: 140,
            sorter: (a, b) => a.nbCompagnies - b.nbCompagnies,
        },
        // {
        //     title: 'Sous-cessions',
        //     key: 'nbSousCessions',
        //     render: (_, record) => (
        //         <Tag color="orange" style={{ fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 8 }}>
        //             {record.nbSousCessions} catégorie{record.nbSousCessions > 1 ? 's' : ''}
        //         </Tag>
        //     ),
        //     align: 'center',
        //     width: 140,
        //     sorter: (a, b) => a.nbSousCessions - b.nbSousCessions,
        // },
        {
            title: 'Total Émissions',
            dataIndex: 'totalEmissionsGlobal',
            key: 'totalEmissionsGlobal',
            render: (montant) => (
                <span style={{ color: '#52c41a', fontWeight: 700, fontSize: 14 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ),
            align: 'right',
            sorter: (a, b) => a.totalEmissionsGlobal - b.totalEmissionsGlobal,
        },
        {
            title: 'Total Sinistres',
            dataIndex: 'totalSinistresGlobal',
            key: 'totalSinistresGlobal',
            render: (montant) => (
                <span style={{ color: '#ff4d4f', fontWeight: 700, fontSize: 14 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ),
            align: 'right',
            sorter: (a, b) => a.totalSinistresGlobal - b.totalSinistresGlobal,
        },
        {
            title: 'Solde Global',
            dataIndex: 'soldeGlobal',
            key: 'soldeGlobal',
            render: (solde) => {
                const isPositif = solde > 0;
                return (
                    <Tag color={isPositif ? 'red' : 'green'} style={{ fontSize: '13px', fontWeight: 700, padding: '6px 12px', borderRadius: 8 }}>
                        {displayNumbers(Math.abs(solde))} F CFA
                        <br />
                        <small style={{ fontWeight: 500 }}>{isPositif ? 'Compagnies' : 'Pool TPV'}</small>
                    </Tag>
                );
            },
            align: 'center',
            sorter: (a, b) => a.soldeGlobal - b.soldeGlobal,
        },
        {
            title: 'Dernière cession',
            dataIndex: 'dateEmission',
            key: 'dateEmission',
            render: (date) => (
                <span style={{ color: '#7f8c8d', fontSize: 12 }}>
                    {new Date(date).toLocaleString('fr-FR')}
                </span>
            ),
            width: 180,
            sorter: (a, b) => new Date(a.dateEmission) - new Date(b.dateEmission),
        },
        {
            title: 'État chèques',
            key: 'etatCheques',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<BankOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        ouvrirEtatChequesModal(record);
                    }}
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderColor: '#667eea',
                        boxShadow: '0 2px 6px rgba(102, 126, 234, 0.35)',
                        fontWeight: 600
                    }}
                >
                    État chèques
                </Button>
            ),
            align: 'center',
            width: 170,
            fixed: 'right',
        },
    ];

    const colonnesCompagnies = [
        {
            title: 'Compagnie',
            dataIndex: 'compagnie',
            key: 'compagnie',
            render: (compagnie) => (
                <Space size={10}>
                    <div style={{
                        width: 34, height: 34, borderRadius: 8,
                        background: '#fff7e6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1px solid #ffe7ba'
                    }}>
                        <BankOutlined style={{ color: '#F39C12', fontSize: 15 }} />
                    </div>
                    <span style={{ fontWeight: 600, color: '#2c3e50' }}>{compagnie.raisonSociale}</span>
                </Space>
            ),
            sorter: (a, b) => String(a.compagnie?.code || "").localeCompare(String(b.compagnie?.code || ""), undefined, { numeric: true }),
        },
        {
            title: 'Catégories',
            key: 'categories',
            render: (_, record) => {
                const labels = [...new Set(
                    (record.sousCessions || []).map((sc) => labelCategorie(sc?.categorie?.codeCategorie ?? sc?.categorieCode))
                )];
                return (
                    <Space wrap>
                        {labels.map((lbl) => (
                            <Tag color="orange" key={lbl}>{lbl}</Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Émissions',
            dataIndex: 'totalEmissionsGlobal',
            key: 'totalEmissionsGlobal',
            render: (montant) => (
                <span style={{ color: '#52c41a', fontWeight: 700 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ),
            align: 'right',
            sorter: (a, b) => a.totalEmissionsGlobal - b.totalEmissionsGlobal,
        },
        {
            // Indemnité de gestion agrégée = Σ des indemnités (15 % des émissions, ou valeur stockée)
            // de chaque sous-cession → cohérent avec le solde global.
            title: 'Indemnité de gestion',
            key: 'indemniteGestionGlobal',
            render: (_, record) => {
                const total = (record.sousCessions || []).reduce(
                    (acc, sc) => acc + indemniteDe(sc.indemnitesEmissions, sc.totalEmissions), 0);
                return (
                    <span style={{ color: '#722ed1', fontWeight: 700 }}>
                        {displayNumbers(total)} F CFA
                    </span>
                );
            },
            align: 'right',
        },
        {
            title: 'Sinistres',
            dataIndex: 'totalSinistresGlobal',
            key: 'totalSinistresGlobal',
            render: (montant) => (
                <span style={{ color: '#ff4d4f', fontWeight: 700 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ),
            align: 'right',
            sorter: (a, b) => a.totalSinistresGlobal - b.totalSinistresGlobal,
        },
        {
            title: 'Solde',
            dataIndex: 'soldeGlobal',
            key: 'soldeGlobal',
            render: (solde) => {
                const isPositif = solde > 0;
                return (
                    <Tag color={isPositif ? 'red' : 'green'} style={{ fontSize: '13px', fontWeight: 700 }}>
                        {displayNumbers(Math.abs(solde))} F CFA
                        <br />
                        <small>{isPositif ? 'Compagnie' : 'Pool TPV'}</small>
                    </Tag>
                );
            },
            align: 'center',
            sorter: (a, b) => a.soldeGlobal - b.soldeGlobal,
        },
        {
            title: 'Date de cession',
            dataIndex: 'dateEmission',
            key: 'dateEmission',
            render: (date) => new Date(date).toLocaleString('fr-FR'),
            width: 170,
            sorter: (a, b) => new Date(a.dateEmission) - new Date(b.dateEmission),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => ouvrirDetailsCession(record)}
                    style={{ background: '#F39C12', borderColor: '#F39C12' }}
                >
                    Détails
                </Button>
            ),
            align: 'center',
            width: 120,
            fixed: 'right',
        },
    ];

    const colonnesSousCessions = [
        {
            title: 'Catégorie',
            dataIndex: 'categorie',
            key: 'categorie',
            render: (categorie) => (
                <Tag color="orange" style={{ fontSize: '13px' }}>
                    {labelCategorie(categorie.codeCategorie)} - {categorie.nom}
                </Tag>
            ),
            width: 100
        },
        {
            title: 'Émissions',
            dataIndex: 'totalEmissions',
            key: 'totalEmissions',
            render: (montant) => (
                <span style={{ color: '#52c41a', fontWeight: 700 }}>
                    {displayNumbers(montant || 0)} F CFA
                </span>
            ),
            align: 'right',
            width: 150,
        },
        {
            // Indemnité de gestion = 15 % des émissions (ou la valeur stockée si renseignée), cohérent
            // avec le calcul du solde (indemniteDe).
            title: 'Indemnité de gestion',
            key: 'indemniteGestion',
            render: (_, record) => (
                <span style={{ color: '#722ed1', fontWeight: 700 }}>
                    {displayNumbers(indemniteDe(record.indemnitesEmissions, record.totalEmissions))} F CFA
                </span>
            ),
            align: 'right',
            width: 160,
        },
        {
            title: 'Annulations',
            dataIndex: 'totalAnnulations',
            key: 'totalAnnulations',
            render: (montant) => (
                <span style={{ color: '#faad14', fontWeight: 700 }}>
                    {displayNumbers(montant || 0)} F CFA
                </span>
            ),
            align: 'right',
            width: 150,
        },
        {
            title: 'Sinistres Payés',
            dataIndex: 'totalSinistres',
            key: 'totalSinistres',
            render: (montant) => (
                <span style={{ color: '#ff4d4f', fontWeight: 700 }}>
                    {displayNumbers(montant || 0)} F CFA
                </span>
            ),
            align: 'right',
            width: 150,
        },
        {
            title: 'Recours Encaissés',
            dataIndex: 'totalRecours',
            key: 'totalRecours',
            render: (montant) => (
                <span style={{ color: '#1890ff', fontWeight: 700 }}>
                    {displayNumbers(montant || 0)} F CFA
                </span>
            ),
            align: 'right',
            width: 150,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const solde = calculerSoldeCession(record);
                const soldeEnFaveurPool = solde < 0;       // la compagnie doit payer le Pool
                const soldeEnFaveurCompagnie = solde > 0;  // le Pool doit payer la compagnie
                const soldeNonNul = soldeEnFaveurPool || soldeEnFaveurCompagnie;
                const isPoolUser = LOGGED_USER?.profil?.organisme?.typeOrganisme === 'POOL';
                // L'ASSIGNEUR = celui qui DOIT PAYER : le Pool si le solde est en faveur de la
                // compagnie, la compagnie si le solde est en faveur du Pool.
                const peutAssigner = (soldeEnFaveurCompagnie && isPoolUser)
                    || (soldeEnFaveurPool && !isPoolUser);
                // Le VALIDATEUR = celui qui REÇOIT le chèque (l'autre partie).
                const peutValider = (soldeEnFaveurCompagnie && !isPoolUser)
                    || (soldeEnFaveurPool && isPoolUser);

                return (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Button
                            type="link"
                            icon={<FileTextOutlined />}
                            onClick={() => ouvrirDetailsSousCession(record)}
                            style={{ color: '#F39C12' }}
                        >
                            Voir les bordereaux
                        </Button>

                        {peutAssigner && !record.numeroCheque && (
                            <Button
                                type="primary"
                                icon={<BankOutlined />}
                                onClick={() => ouvrirModalCheque(record)}
                                style={{ background: '#52c41a', borderColor: '#52c41a', width: '100%' }}
                            >
                                Assigner chèque
                            </Button>
                        )}

                        {/* La compagnie peut corriger les coordonnées du chèque avant la rétrocession. */}
                        {!isPoolUser && soldeEnFaveurPool && record.numeroCheque && (
                            <Button
                                type="primary"
                                icon={<BankOutlined />}
                                onClick={() => ouvrirModalCheque(record)}
                                style={{ background: '#1677ff', borderColor: '#1677ff', width: '100%' }}
                            >
                                Modifier le chèque
                            </Button>
                        )}

                        {/* Affichage des informations de chèque si assigné (peu importe le sens du solde) */}
                        {soldeNonNul && record.numeroCheque && (
                            <div>
                                <div style={{
                                    background: record.validationCheque
                                        ? 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)'
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    borderRadius: '10px',
                                    padding: '10px 14px',
                                    color: 'white',
                                    fontSize: '12px',
                                    boxShadow: record.validationCheque
                                        ? '0 3px 10px rgba(82, 196, 26, 0.35)'
                                        : '0 3px 10px rgba(102, 126, 234, 0.35)',
                                    textAlign: 'left',
                                    position: 'relative'
                                }}>
                                    {record.validationCheque && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '8px',
                                            right: '8px',
                                            background: 'rgba(255, 255, 255, 0.3)',
                                            borderRadius: '50%',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <CheckOutlined style={{ fontSize: '14px', color: 'white' }} />
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                        <BankOutlined style={{ marginRight: '8px', fontSize: '16px' }} />
                                        <strong style={{ fontSize: '13px' }}>Chèque N° {record.numeroCheque}</strong>
                                    </div>
                                    <div style={{ fontSize: '11px', opacity: 0.95 }}>
                                        🏦 {record.banque}
                                    </div>
                                    <div style={{ fontSize: '11px', opacity: 0.95, marginTop: '2px' }}>
                                        📅 {new Date(record.dateCheque).toLocaleDateString('fr-FR')}
                                    </div>
                                    <div style={{ fontSize: '11px', opacity: 0.95, marginTop: '2px', fontWeight: 600 }}>
                                        💰 {displayNumbers(record.montantCheque)} F CFA
                                    </div>

                                    {record.dateValidationCheque && (
                                        <div style={{
                                            marginTop: '6px',
                                            paddingTop: '6px',
                                            borderTop: '1px solid rgba(255, 255, 255, 0.3)',
                                            fontSize: '10px',
                                            opacity: 0.9
                                        }}>
                                            ✅ Validé par {soldeEnFaveurPool ? 'le Pool' : 'la Compagnie'} <br />
                                            {/* Date validation */}🗓️ le {new Date(record.dateValidationCheque).toLocaleDateString('fr-FR')}
                                        </div>
                                    )}
                                </div>

                                {/* Bouton de validation pour le RECEVEUR (Pool ou compagnie selon le sens du solde) */}
                                {peutValider && !record.dateValidationCheque && (
                                    <Popconfirm
                                        title="Validation du chèque"
                                        description={`Confirmer la réception du chèque N° ${record.numeroCheque} d'un montant de ${displayNumbers(record.montantCheque)} F CFA ?`}
                                        onConfirm={() => validerCheque(record)}
                                        okText="Oui, valider"
                                        cancelText="Annuler"
                                        okButtonProps={{
                                            style: { background: '#52c41a', borderColor: '#52c41a' },
                                            loading: loadingValidation[record.codeId]
                                        }}
                                    >
                                        <Button
                                            type="primary"
                                            icon={<CheckOutlined />}
                                            style={{
                                                background: '#52c41a',
                                                borderColor: '#52c41a',
                                                marginTop: '8px',
                                                width: '100%'
                                            }}
                                            size="small"
                                            loading={loadingValidation[record.codeId]}
                                        >
                                            Valider la réception du chèque
                                        </Button>
                                    </Popconfirm>
                                )}
                            </div>
                        )}
                        {/* Annuler la cession (Pool, HOP119) — tant que le chèque n'est pas encaissé.
                            Le refus « déjà rétrocédée » est renvoyé par le backend (409). */}
                        {isPoolUser && hasHOP119 && !record.dateValidationCheque && (
                            <Button
                                danger
                                size="small"
                                icon={<CloseCircleOutlined />}
                                style={{ marginTop: '8px', width: '100%' }}
                                onClick={() => { setAnnulCession(record); setAnnulMotif(''); }}
                            >
                                Annuler la cession
                            </Button>
                        )}
                    </Space>
                );
            },
            align: 'center',
            width: 300,
        },
    ];

    // Niveau le plus profond : sous-cessions (catégories) d'une compagnie
    const expandedCompagnieRender = (record) => {
        return (
            <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px' }}>
                <h5 style={{ marginBottom: '12px', color: '#F39C12', fontWeight: 600 }}>
                    Sous-cessions par catégorie ({record.sousCessions.length})
                </h5>
                <Table
                    columns={colonnesSousCessions}
                    dataSource={record.sousCessions}
                    pagination={false}
                    rowKey="codeId"
                    size="small"
                    bordered
                />
            </div>
        );
    };

    // Niveau intermédiaire : compagnies pour un mois donné
    const expandedRowRender = (record) => {
        const [annee, moisNum] = record.moisRapport.split('-');
        return (
            <div style={{
                padding: '20px 24px',
                background: 'linear-gradient(180deg, #fff8e7 0%, #fafafa 100%)',
                borderLeft: '4px solid #F39C12'
            }}>
                <h4 style={{
                    marginBottom: '16px',
                    color: '#F39C12',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontWeight: 700
                }}>
                    <BankOutlined />
                    Compagnies — {frenchMonth(moisNum)} {annee}
                    <Tag color="geekblue" style={{ marginLeft: 8, fontWeight: 600 }}>
                        {record.nbCompagnies} compagnie{record.nbCompagnies > 1 ? 's' : ''}
                    </Tag>
                </h4>
                <Table
                    columns={colonnesCompagnies}
                    dataSource={record.compagnies}
                    pagination={false}
                    rowKey="key"
                    size="middle"
                    bordered
                    expandable={{
                        expandedRowRender: expandedCompagnieRender,
                    }}
                />
            </div>
        );
    };

    const ouvrirDetailsCession = (groupe) => {
        // Cartes du modal = catégories AFFICHÉES (A/B/T fusionnées en « TPV »). On ne mute PAS `groupe`
        // (partagé avec la ligne compagnie / le flux chèque) : on clone avec les sous-cessions fusionnées.
        setSelectedCessionGroup({
            ...groupe,
            sousCessions: fusionnerSousCessionsParLibelle(groupe?.sousCessions),
        });
        setDetailModalVisible(true);
    };

    // Compte de cession ALIGNÉ sur /retrocessions (document generer-compte-cession) : base PERSISTÉE
    // (totalEmissionsCorrige ?? totalEmissions) MOINS la prime reportée, indemnités 15 %, sinistres,
    // solde = émissions − sinistres − indemnités. Aucune re-somme des lignes (≠ calculerTotauxCession).
    const calculerCompteCessionRetroAligne = (cession) => {
        // Version CORRIGÉE (comme la rétro) par défaut ; le toggle « compagnie » (voirCorrigeCession=false)
        // revient à l'originale. Sans correction (totalEmissionsCorrige null), les deux valent le total.
        const corrige = cession?.totalEmissionsCorrige;
        const original = Number(cession?.totalEmissions) || 0;
        const cede = (voirCorrigeCession && corrige != null) ? Number(corrige) : original;
        const reportee = Number(cession?.totalEmissionsReportees) || 0;
        const totalEmissions = cede - reportee;
        const indemnEmissions = Math.round(totalEmissions * 0.15);
        const totalSinistres = Number(cession?.totalSinistres) || 0;
        // Pas d'annulations ni de recours dans le compte de cession côté rétrocession.
        const debit = indemnEmissions + totalSinistres;
        const credit = totalEmissions;
        const solde = debit - credit; // = −(émissions − sinistres − indemnités)
        return {
            cede,
            reportee,
            totalEmissions,
            totalAnnulations: 0,
            indemnitesEmissions: indemnEmissions,
            indemnitesAnnulations: 0,
            totalSinistres,
            totalRecours: 0,
            debit,
            credit,
            solde,
            soldeEnFaveurDe: solde > 0 ? 'Compagnie' : 'Pool TPV',
            soldeMontant: Math.abs(solde),
        };
    };

    const calculerTotauxCession = (cession) => {
        // La cession porte sur la PRIME NETTE CÉDÉE = PN Pool UNIQUEMENT (la part compagnie, en
        // co-assurance, est RETENUE et n'est pas cédée ; en TPV pur elle vaut 0). On la recalcule
        // depuis les lignes DÉCOMPOSÉES (decomposerItemCession — contrat embarqué dans chaque item),
        // afin d'afficher la nette même pour une cession enregistrée en TTC. Indemnités = 15 % de la nette.
        const netteDe = (it) => Number(it?.primeNettePool) || 0;
        const emItems = enrichirItemsCession(cession?.bordereauEmissions?.items);
        const anItems = enrichirItemsCession(cession?.bordereauAnnulations?.items);
        const totalEmissions = emItems.reduce((s, it) => s + netteDe(it), 0);
        const totalAnnulations = Math.abs(anItems.reduce((s, it) => s + netteDe(it), 0));
        const totalSinistres = cession.totalSinistres || 0;
        const totalRecours = cession.totalRecours || 0;
        const indemnEmissions = totalEmissions * 0.15;
        const indemnAnnulations = totalAnnulations * 0.15;
        const debit = totalAnnulations + indemnEmissions + totalSinistres;
        const credit = totalEmissions + indemnAnnulations + totalRecours;
        const solde = debit - credit;

        return {
            totalEmissions,
            totalAnnulations,
            indemnitesEmissions: indemnEmissions,
            indemnitesAnnulations: indemnAnnulations,
            totalSinistres,
            totalRecours,
            debit,
            credit,
            solde,
            soldeEnFaveurDe: solde > 0 ? 'Compagnie' : 'Pool TPV',
            soldeMontant: Math.abs(solde)
        };
    };

    const primesColumns = [
        {
            title: "N° Police",
            dataIndex: "numeroPolice",
            key: "numeroPolice",
        },
        {
            title: "Avenant",
            dataIndex: "avenant",
            key: "avenant",
        },
        {
            title: "Assuré",
            dataIndex: "assure",
            key: "assure",
        },
        {
            title: "Effet",
            dataIndex: "effet",
            key: "effet",
            render: (effet) => <span>{fmtDateFr(effet)}</span>,
        },
        {
            title: "Échéance",
            dataIndex: "echeance",
            key: "echeance",
            render: (echeance) => <span>{fmtDateFr(echeance)}</span>,
        },
        {
            title: "Catégorie",
            dataIndex: "categorie",
            key: "categorie",
            render: (v) => labelCategorie(v),
        },
        // Décomposition du montant cédé (dérivée par decomposerItemCession, somme = Prime TTC).
        // Valeur absolue : cohérent avec l'ancienne colonne unique (annulations en magnitude).
        {
            title: "PN Pool", dataIndex: "primeNettePool", key: "primeNettePool", align: "right",
            render: (v) => <span>{displayNumbers(Math.abs(v || 0))}</span>,
        },
        {
            title: "PN Compagnie", dataIndex: "primeNetteCompagnie", key: "primeNetteCompagnie", align: "right",
            render: (v) => <span>{displayNumbers(Math.abs(v || 0))}</span>,
        },
        {
            title: "Accessoires", dataIndex: "accessoires", key: "accessoires", align: "right",
            render: (v) => <span>{displayNumbers(Math.abs(v || 0))}</span>,
        },
        {
            title: "Taxes", dataIndex: "taxes", key: "taxes", align: "right",
            render: (v) => <span>{displayNumbers(Math.abs(v || 0))}</span>,
        },
        {
            title: "Prime TTC", dataIndex: "primeACeder", key: "primeACeder", align: "right",
            render: (v) => <span><strong>{displayNumbers(Math.abs(v || 0))}</strong> F CFA</span>,
        },
    ];

    const sinistresColumns = [
        {
            title: "N° Sinistre",
            dataIndex: "numeroSinistre",
            key: "numeroSinistre",
            render: (_, record) => <span>{record?.sinistreNumeroSinistre || record?.sinistreNumSinistre}</span>
        },
        {
            title: "N° Police",
            dataIndex: "numeroPolice",
            key: "numeroPolice",
            render: (_, record) => <span>{record?.sinistreNumPolice || record?.sinistreNumPoliceP}</span>
        },
        {
            title: "Assuré",
            dataIndex: "sinistreSouscripteur",
            key: "sinistreSouscripteur",
        },
        {
            title: "Date de survenance",
            dataIndex: "dateSinistre",
            key: "dateSinistre",
            render: (v) => <span>{v ? frenchDateOnly(v) : ""}</span>,
        },
        {
            title: "Catégorie",
            key: "categorie",
            render: (_, record) => <span>{labelCategorie(selectedCessionGroup?.cessionActive?.categorieCode)}</span>
        },
        {
            title: "Justificatif",
            dataIndex: "justificatif",
            key: "justificatif",
            render: (_, record) => {
                const j = formatJustificatifCheque(record);
                return j ? <span style={{ whiteSpace: "pre-line" }}>{j}</span> : <span style={{ color: "#bbb" }}>—</span>;
            },
        },
        {
            title: "Montant",
            dataIndex: "montant",
            key: "montant",
            render: (montant) => <span>{displayNumbers(montant)} F CFA</span>
        },
    ];

    const handleExport = () => {
        const data = cessions.map((cession) => {
            const { totalEmissions, totalAnnulations, indemnitesEmissions, indemnitesAnnulations, totalSinistres, totalRecours } = calculerTotauxCession(cession);
            return {
                ...cession,
                totalEmissions,
                totalAnnulations,
                indemnitesEmissions,
                indemnitesAnnulations,
                totalSinistres,
                totalRecours,
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Cessions");
        XLSX.writeFile(workbook, "cessions.xlsx");
    };

    // Fonction pour ouvrir le modal de chèque
    const ouvrirModalCheque = (cession) => {
        // Solde de la SOUS-CESSION (net des indemnités de gestion) : c'est LUI qui doit
        // pré-remplir le montant du chèque, pas le montant total des émissions.
        const solde = calculerSoldeCession(cession);

        // Un chèque est assignable dès que le solde est NON NUL : le sens détermine QUI paie
        // (solde < 0 → la compagnie paie le Pool ; solde > 0 → le Pool paie la compagnie). Le
        // bouton « Assigner chèque » n'est de toute façon affiché qu'au payeur concerné.
        if (solde !== 0) {
            setSelectedCessionForCheque({
                ...cession,
                soldeMontant: Math.abs(solde)
            });
            setChequeModalVisible(true);
        } else {
            toast.warning("Aucun solde à régler pour cette cession.");
        }
    };

    // Fonction de soumission du chèque
    const handleAssignerCheque = async (chequeData) => {
        try {
            await axiosInstance.put('/api/cession/assigner-cheque', chequeData);
            toast.success('Chèque assigné avec succès');
            setChequeModalVisible(false);
            setSelectedCessionForCheque(null);
            chargerCessionsValidees(); // Recharger les données
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Erreur lors de l\'assignation du chèque');
        }
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <style>{`
        .pool-header-card {
          background: linear-gradient(135deg, #F39C12 0%, #E67E22 100%);
          border-radius: 16px;
          padding: 32px;
          margin-bottom: 24px;
          box-shadow: 0 4px 20px rgba(243, 156, 18, 0.25);
          color: white;
        }

        .pool-header-card h2 {
          color: white;
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 8px 0;
        }

        .pool-header-card p {
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
          font-size: 15px;
        }

        .pool-filter-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
        }

        .pool-stats-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
        }

        .ant-table-expanded-row > td {
          padding: 0 !important;
        }

        .detail-modal .ant-modal-content {
          border-radius: 16px;
        }

        .detail-modal .ant-modal-header {
          background: linear-gradient(135deg, #F39C12 0%, #E67E22 100%);
          border-radius: 16px 16px 0 0;
        }

        .detail-modal .ant-modal-title {
          color: white;
          font-size: 18px;
          font-weight: 600;
        }

        .sous-cession-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
          border: 2px solid #e9ecef;
          transition: all 0.3s ease;
        }

        .sous-cession-card:hover {
          border-color: #F39C12;
          box-shadow: 0 4px 12px rgba(243, 156, 18, 0.15);
        }

        .sous-cession-card.active {
          border-color: #F39C12;
          background: #fff;
          box-shadow: 0 4px 12px rgba(243, 156, 18, 0.15);
        }

        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          border-radius: 12px;
        }
      `}</style>

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: '#f5f5f5' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête Pool */}
                            <div className="pool-header-card">
                                <h2>Cessions Validées</h2>
                                <p>Consultation des cessions mensuelles validées par les compagnies d'assurance</p>
                            </div>

                            {/* Filtres */}
                            <div className="pool-filter-card">
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={8}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            Période
                                        </label>
                                        <DatePicker
                                            placeholder="Filtrer par mois"
                                            onChange={(_, dateString) => setSelectedPeriode(dateString)}
                                            picker="month"
                                            style={{ width: '100%' }}
                                        />
                                    </Col>
                                    {LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" && (<Col xs={24} sm={12} md={8}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            Compagnie
                                        </label>
                                        <Select
                                            placeholder="Filtrer par compagnie"
                                            style={{ width: "100%" }}
                                            onChange={setSelectedCompagnie}
                                            allowClear
                                            options={[...compagnies]
                                                .sort((a, b) => String(a.code || "").localeCompare(String(b.code || ""), undefined, { numeric: true }))
                                                .map((com) => ({
                                                    value: com.code,
                                                    label: com.raisonSociale,
                                                }))}
                                        />
                                    </Col>)}
                                    <Col xs={24} sm={12} md={8}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            &nbsp;
                                        </label>
                                        <Button
                                            type="primary"
                                            icon={<ReloadOutlined />}
                                            onClick={chargerCessionsValidees}
                                            style={{ width: '100%', background: '#F39C12', borderColor: '#F39C12' }}
                                            loading={loading}
                                        >
                                            Actualiser
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Statistiques globales */}
                            {cessionsGroupees.length > 0 && (
                                <div className="pool-stats-card">
                                    <Row gutter={16}>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Mois couverts"
                                                value={cessionsGroupees.length}
                                                prefix={<CalendarOutlined />}
                                                valueStyle={{ color: '#F39C12' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Sous-cessions"
                                                value={cessionsGroupees.reduce((acc, g) => acc + g.nbSousCessions, 0)}
                                                prefix={<FileTextOutlined />}
                                                valueStyle={{ color: '#52c41a' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Total Émissions"
                                                value={displayNumbers(cessionsGroupees.reduce((acc, g) => acc + g.totalEmissionsGlobal, 0))}
                                                suffix="F CFA"
                                                valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Total Sinistres"
                                                value={displayNumbers(cessionsGroupees.reduce((acc, g) => acc + g.totalSinistresGlobal, 0))}
                                                suffix="F CFA"
                                                valueStyle={{ color: '#ff4d4f', fontSize: '18px' }}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Table des cessions */}
                            <Card
                                title="Liste des cessions validées"
                                extra={
                                    <Space>
                                        <Button onClick={handleExport} icon={<DownloadOutlined />} type="dashed">
                                            Exporter
                                        </Button>
                                    </Space>
                                }
                                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
                            >
                                <ConfigProvider locale={frFR}>
                                    <Table
                                        columns={colonnesPrincipales}
                                        dataSource={cessionsGroupees}
                                        loading={loading}
                                        expandable={{
                                            expandedRowRender,
                                            expandedRowKeys,
                                            onExpand: (expanded, record) => {
                                                setExpandedRowKeys(expanded ? [record.key] : []);
                                            },
                                        }}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total: ${total} cession(s)`,
                                        }}
                                        scroll={{ x: 1200 }}
                                    />
                                </ConfigProvider>
                            </Card>

                        </div>

                        <Footer />
                    </div>
                </div>
            </div>

            {/* Modal de détails */}
            <Modal
                title={
                    selectedCessionGroup && (
                        <span>
                            Détails de la cession - {selectedCessionGroup?.cessionActive?.compagnieNom} - {' '}
                            {frenchMonth(selectedCessionGroup?.cessionActive?.moisRapport?.split('-')?.[1])} {selectedCessionGroup?.cessionActive?.moisRapport?.split('-')?.[0]}
                        </span>
                    )
                }
                open={detailModalVisible}
                onCancel={() => {
                    setDetailModalVisible(false);
                    setSelectedCessionGroup(null);
                }}
                width="95%"
                style={{ top: 20 }}
                footer={null}
                className="detail-modal"
            >
                {selectedCessionGroup && (
                    <div style={{ position: 'relative' }}>
                        {loadingDetails && (
                            <div className="loading-overlay">
                                <Spin size="large" tip="Chargement des détails..." />
                            </div>
                        )}

                        {/* Sélection de sous-cession */}
                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{ marginBottom: '16px', color: '#F39C12' }}>
                                {selectedCessionGroup?.sousCessions ? "Sélectionner une catégorie" : `Categorie : ${labelCategorie(selectedCessionGroup?.cessionActive?.categorieCode)}`}
                            </h4>
                            <Row gutter={[16, 16]}>
                                {selectedCessionGroup?.sousCessions?.map((sc) => (
                                    <Col xs={24} sm={12} md={8} lg={6} key={sc.categorieCode}>
                                        <div
                                            className={`sous-cession-card ${selectedCessionGroup?.cessionActive?.categorieCode === sc.categorieCode ? 'active' : ''
                                                }`}
                                            onClick={() => ouvrirDetailsSousCession(sc)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <h5 style={{ margin: '0 0 12px 0', color: '#F39C12' }}>
                                                {labelCategorie(sc.categorie.codeCategorie)}
                                            </h5>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                <div>Émissions: {displayNumbers(sc?.totalEmissions || 0)} F</div>
                                                <div>Sinistres: {displayNumbers(sc?.totalSinistres || 0)} F</div>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>

                        {/* Détails de la sous-cession sélectionnée */}
                        {selectedCessionGroup.cessionActive && (
                            <ConfigProvider locale={frFR}>
                                <Tabs
                                    type="card"
                                    items={[
                                        {
                                            label: "Bordereau des émissions",
                                            key: "1",
                                            children: (
                                                <BordereauEmissions
                                                    ref={printEmissionsRef}
                                                    data={enrichirItemsCession(selectedCessionGroup.cessionActive.bordereauEmissions?.items)}
                                                    columns={primesColumns}
                                                    selectedRows={[]}
                                                    onSelectionChange={() => { }}
                                                    onValidate={() => { }}
                                                    onPrint={handlePrintEmissions}
                                                    isValidated={true}
                                                    isDisabled={true}
                                                    loading={false}
                                                    categorie={{ codeCategorie: selectedCessionGroup.cessionActive.categorieCode }}
                                                    periode={selectedCessionGroup?.cessionActive?.moisRapport}
                                                    compagnie={{ raisonSociale: selectedCessionGroup?.cessionActive?.compagnieNom }}
                                                />
                                            ),
                                        },
                                        {
                                            label: "Bordereau des annulations",
                                            key: "2",
                                            children: (
                                                <BordereauAnnulations
                                                    ref={printAnnulationsRef}
                                                    data={enrichirItemsCession(selectedCessionGroup.cessionActive.bordereauAnnulations?.items)}
                                                    columns={primesColumns}
                                                    selectedRows={[]}
                                                    onSelectionChange={() => { }}
                                                    onValidate={() => { }}
                                                    onPrint={handlePrintAnnulations}
                                                    isValidated={true}
                                                    isDisabled={true}
                                                    loading={false}
                                                    categorie={{ codeCategorie: selectedCessionGroup.cessionActive.categorieCode }}
                                                    periode={selectedCessionGroup?.cessionActive?.moisRapport}
                                                    compagnie={{ raisonSociale: selectedCessionGroup?.cessionActive?.compagnieNom }}
                                                />
                                            ),
                                        },
                                        {
                                            label: "Bordereau des sinistres payés",
                                            key: "3",
                                            children: (
                                                <BordereauSinistresPayes
                                                    ref={printSinistresRef}
                                                    data={selectedCessionGroup.cessionActive.bordereauSinistresPayes?.items || []}
                                                    columns={sinistresColumns}
                                                    selectedRows={[]}
                                                    onSelectionChange={() => { }}
                                                    onValidate={() => { }}
                                                    onPrint={handlePrintSinistres}
                                                    isValidated={true}
                                                    isDisabled={true}
                                                    loading={false}
                                                    categorie={{ codeCategorie: selectedCessionGroup.cessionActive.categorieCode }}
                                                    periode={selectedCessionGroup?.cessionActive?.moisRapport}
                                                    compagnie={{ raisonSociale: selectedCessionGroup?.cessionActive?.compagnieNom }}
                                                />
                                            ),
                                        },
                                        {
                                            label: "Bordereau des recours encaissés",
                                            key: "4",
                                            children: (
                                                <BordereauRecoursEncaisses
                                                    ref={printRecoursRef}
                                                    data={selectedCessionGroup.cessionActive.bordereauRecoursEncaisses?.items || []}
                                                    columns={sinistresColumns}
                                                    selectedRows={[]}
                                                    onSelectionChange={() => { }}
                                                    onValidate={() => { }}
                                                    onPrint={handlePrintRecours}
                                                    isValidated={true}
                                                    isDisabled={true}
                                                    loading={false}
                                                    categorie={{ codeCategorie: selectedCessionGroup.cessionActive.categorieCode }}
                                                    periode={selectedCessionGroup?.cessionActive?.moisRapport}
                                                    compagnie={{ raisonSociale: selectedCessionGroup?.cessionActive?.compagnieNom }}
                                                />
                                            ),
                                        },
                                        {
                                            label: "Compte de cession",
                                            key: "5",
                                            children: (
                                                <div>
                                                    {/* Anomalie NON corrigée : proposer la correction (HOP112) — prend effet dans la rétrocession. */}
                                                    {!correctionCessionActive?.corrige && anomaliesCessionActive.length > 0 && (
                                                        <Alert
                                                            type="error"
                                                            showIcon
                                                            style={{ marginBottom: 12 }}
                                                            message="Lignes de cession anomales (contrat sans date d'effet/échéance)"
                                                            description={
                                                                <div>
                                                                    <div style={{ marginBottom: 8 }}>
                                                                        Ces lignes ne sont pas cessibles/rétrocédables. Le Pool (HOP112) peut corriger le
                                                                        compte de cession pour les écarter — l'original (version compagnie) est conservé, et la
                                                                        correction <strong>prend effet directement dans la rétrocession</strong>.
                                                                    </div>
                                                                    <div style={{ fontSize: 12, marginBottom: 8 }}>
                                                                        Ligne(s) : {anomaliesCessionActive.map((l) => `${l.numeroPolice} (${displayNumbers(Math.round(l.primeNettePool || 0))} F)`).join(', ')}
                                                                    </div>
                                                                    {correctionCessionActive?.figee ? (
                                                                        <Tooltip title={!hasHOP112 ? "Habilitation HOP112 requise" : "Rétrocession déjà validée — correction EXCEPTIONNELLE (forçage)"}>
                                                                            <Button danger loading={savingCorrection}
                                                                                disabled={!hasHOP112}
                                                                                onClick={() => corrigerAnomalieCession(true)}>
                                                                                Forcer la correction (rétro validée)
                                                                            </Button>
                                                                        </Tooltip>
                                                                    ) : (
                                                                        <Tooltip title={!hasHOP112 ? "Habilitation HOP112 requise" : ""}>
                                                                            <Button danger type="primary" loading={savingCorrection}
                                                                                disabled={!hasHOP112}
                                                                                onClick={() => corrigerAnomalieCession(false)}>
                                                                                Corriger le compte de cession
                                                                            </Button>
                                                                        </Tooltip>
                                                                    )}
                                                                </div>
                                                            }
                                                        />
                                                    )}
                                                    {correctionCessionActive?.corrige && (
                                                        <Alert
                                                            type="warning"
                                                            showIcon
                                                            style={{ marginBottom: 12 }}
                                                            message="Compte corrigé par le Pool (anomalie : contrat sans date d'effet/échéance)"
                                                            description={
                                                                <div>
                                                                    <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                                                        <span>Version affichée :</span>
                                                                        <Segmented
                                                                            size="small"
                                                                            value={voirCorrigeCession ? 'corrige' : 'compagnie'}
                                                                            onChange={(v) => setVoirCorrigeCession(v === 'corrige')}
                                                                            options={[
                                                                                { label: 'Compte compagnie', value: 'compagnie' },
                                                                                { label: 'Compte corrigé (Pool)', value: 'corrige' },
                                                                            ]}
                                                                        />
                                                                    </div>
                                                                    <div style={{ fontSize: 12 }}>
                                                                        Ligne(s) écartée(s) : {(correctionCessionActive.lignesEcartees || []).map((l) => `${l.numeroPolice} (${displayNumbers(Math.round(l.primeNettePool || 0))} F)`).join(', ') || '—'}
                                                                    </div>
                                                                    <div style={{ marginTop: 8 }}>
                                                                        <Tooltip title={correctionCessionActive?.figee ? "Rétrocession validée : correction figée" : (!hasHOP112 ? "Habilitation HOP112 requise" : "")}>
                                                                            <Button size="small" loading={savingCorrection}
                                                                                disabled={!hasHOP112 || correctionCessionActive?.figee}
                                                                                onClick={annulerCorrectionCession}>
                                                                                Annuler la correction
                                                                            </Button>
                                                                        </Tooltip>
                                                                    </div>
                                                                </div>
                                                            }
                                                        />
                                                    )}
                                                    <CompteDeCession
                                                        ref={printCessionRef}
                                                        validationState={{
                                                            emissions: { validated: true, data: selectedCessionGroup.cessionActive.bordereauEmissions?.items || [] },
                                                            annulations: { validated: true, data: selectedCessionGroup.cessionActive.bordereauAnnulations?.items || [] },
                                                            sinistresPayes: { validated: true, data: selectedCessionGroup.cessionActive.bordereauSinistresPayes?.items || [] },
                                                            recoursEncaisses: { validated: true, data: selectedCessionGroup.cessionActive.bordereauRecoursEncaisses?.items || [] },
                                                            cessionValidated: true,
                                                        }}
                                                        cessionExistante={selectedCessionGroup.cessionActive}
                                                        totals={calculerCompteCessionRetroAligne(selectedCessionGroup.cessionActive)}
                                                        variant="retro"
                                                        onValidate={() => { }}
                                                        onPrint={handlePrintCession}
                                                        isValidated={true}
                                                        isDisabled={true}
                                                        loading={false}
                                                        categorie={{ codeCategorie: selectedCessionGroup.cessionActive.categorieCode }}
                                                        periode={selectedCessionGroup?.cessionActive?.moisRapport}
                                                        compagnie={{ code: selectedCessionGroup?.cessionActive?.compagnie?.code, raisonSociale: selectedCessionGroup?.cessionActive?.compagnieNom }}
                                                    />
                                                </div>
                                            ),
                                        },
                                    ]}
                                />
                            </ConfigProvider>
                        )}
                    </div>
                )}
            </Modal>
            {/* Modal État des chèques émis (par mois) */}
            <Modal
                title={
                    selectedMoisForChequesEtat && (
                        <Space>
                            <BankOutlined style={{ color: '#fff' }} />
                            <span>
                                État des chèques émis — {frenchMonth(selectedMoisForChequesEtat.moisRapport?.split('-')?.[1])} {selectedMoisForChequesEtat.moisRapport?.split('-')?.[0]}
                            </span>
                        </Space>
                    )
                }
                open={chequesEtatModalVisible}
                onCancel={fermerEtatChequesModal}
                width={1100}
                footer={null}
                className="detail-modal"
            >
                {selectedMoisForChequesEtat && (() => {
                    const { rows, totalMontant } = buildChequesRows(selectedMoisForChequesEtat);
                    const colsApercu = [
                        {
                            title: 'N°',
                            key: 'index',
                            render: (_, __, idx) => idx + 1,
                            width: 60,
                            align: 'center',
                        },
                        {
                            title: 'Compagnie',
                            dataIndex: 'compagnie',
                            key: 'compagnie',
                            render: (val) => <strong style={{ color: '#2c3e50' }}>{val}</strong>,
                        },
                        {
                            title: 'Catégorie',
                            dataIndex: 'categorie',
                            key: 'categorie',
                            align: 'center',
                            width: 110,
                            render: (val) => val !== '—'
                                ? <Tag color="orange">{val}</Tag>
                                : <span style={{ color: '#bbb' }}>—</span>,
                        },
                        {
                            title: 'N° Chèque',
                            dataIndex: 'numeroCheque',
                            key: 'numeroCheque',
                            render: (val) => val !== '—'
                                ? <span style={{ fontWeight: 600 }}>{val}</span>
                                : <span style={{ color: '#bbb' }}>—</span>,
                        },
                        {
                            title: 'Banque',
                            dataIndex: 'banque',
                            key: 'banque',
                            render: (val) => val !== '—'
                                ? <Space><BankOutlined style={{ color: '#F39C12' }} />{val}</Space>
                                : <span style={{ color: '#bbb' }}>—</span>,
                        },
                        {
                            title: 'Montant',
                            dataIndex: 'montantCheque',
                            key: 'montantCheque',
                            align: 'right',
                            render: (val) => (
                                <span style={{ color: val > 0 ? '#52c41a' : '#bbb', fontWeight: 700 }}>
                                    {displayNumbers(val || 0)} F CFA
                                </span>
                            ),
                        },
                    ];

                    return (
                        <div>
                            {/* Bandeau résumé */}
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: 12,
                                padding: '18px 22px',
                                color: 'white',
                                marginBottom: 18,
                                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.35)'
                            }}>
                                <Row gutter={16} align="middle">
                                    <Col xs={24} sm={12}>
                                        <div style={{ fontSize: 12, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Période
                                        </div>
                                        <div style={{ fontSize: 18, fontWeight: 700 }}>
                                            {frenchMonth(selectedMoisForChequesEtat.moisRapport?.split('-')?.[1])} {selectedMoisForChequesEtat.moisRapport?.split('-')?.[0]}
                                        </div>
                                        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                                            {selectedMoisForChequesEtat.nbCompagnies} compagnie{selectedMoisForChequesEtat.nbCompagnies > 1 ? 's' : ''} — {rows.filter(r => r.numeroCheque !== '—').length} chèque{rows.filter(r => r.numeroCheque !== '—').length > 1 ? 's' : ''}
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: 12, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Total Chèques
                                        </div>
                                        <div style={{ fontSize: 24, fontWeight: 800 }}>
                                            {displayNumbers(totalMontant)} F CFA
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Aperçu */}
                            <Table
                                columns={colsApercu}
                                dataSource={rows}
                                rowKey="key"
                                pagination={false}
                                size="middle"
                                bordered
                                summary={() => (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row style={{ background: '#fafafa' }}>
                                            <Table.Summary.Cell index={0} colSpan={5} align="right">
                                                <strong>TOTAL</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                <strong style={{ color: '#52c41a' }}>
                                                    {displayNumbers(totalMontant)} F CFA
                                                </strong>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                )}
                            />

                            {/* Boutons d'export */}
                            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <Button
                                    icon={<FileExcelOutlined />}
                                    onClick={() => exporterChequesEtatExcel(selectedMoisForChequesEtat)}
                                    style={{
                                        background: '#1f7a3f',
                                        borderColor: '#1f7a3f',
                                        color: 'white',
                                        fontWeight: 600
                                    }}
                                    size="large"
                                >
                                    Exporter Excel
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<FilePdfOutlined />}
                                    onClick={lancerImpressionChequesEtat}
                                    style={{
                                        background: '#c0392b',
                                        borderColor: '#c0392b',
                                        fontWeight: 600
                                    }}
                                    size="large"
                                >
                                    Imprimer / PDF
                                </Button>
                            </div>

                            {/* Version cachée pour impression PDF */}
                            <div style={{
                                position: isPrintingCheques ? 'relative' : 'absolute',
                                left: isPrintingCheques ? 'auto' : '-9999px',
                                visibility: isPrintingCheques ? 'visible' : 'hidden'
                            }}>
                                <style>
                                    {`
                                        @media print {
                                            @page {
                                                size: portrait;
                                                margin: 10mm;
                                            }
                                            body {
                                                print-color-adjust: exact;
                                                -webkit-print-color-adjust: exact;
                                            }
                                            strong, b, th, .fw-bold {
                                                font-weight: bold !important;
                                            }
                                        }
                                    `}
                                </style>
                                <EtatChequesEmis
                                    ref={printChequesEtatRef}
                                    periode={selectedMoisForChequesEtat.moisRapport}
                                    rows={rows}
                                    totalMontant={totalMontant}
                                />
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {chequeModalVisible && <ChequeAssignmentModal visible={chequeModalVisible} onCancel={() => setChequeModalVisible(false)} onSubmit={handleAssignerCheque} cessionData={selectedCessionForCheque} banques={banques} />}

            {/* Annulation d'une cession (Pool, HOP119) — motif obligatoire. */}
            <Modal
                open={!!annulCession}
                title="Annuler la cession"
                okText="Annuler la cession"
                okType="danger"
                cancelText="Fermer"
                confirmLoading={annulLoading}
                onOk={confirmerAnnulationCession}
                onCancel={() => { setAnnulCession(null); setAnnulMotif(''); }}
                okButtonProps={{ disabled: !annulMotif.trim() }}
                destroyOnClose
            >
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Les éléments cédés redeviendront « non cédés » (ré-cessibles)."
                    description="Action possible uniquement si la période n'est pas encore rétrocédée et si le chèque n'est pas encaissé."
                />
                <div style={{ marginBottom: 6, fontWeight: 600 }}>Motif de l'annulation (obligatoire)</div>
                <Input.TextArea
                    rows={3}
                    value={annulMotif}
                    onChange={(e) => setAnnulMotif(e.target.value)}
                    placeholder="Ex : erreur de saisie, cession en double, correction de périmètre…"
                    maxLength={500}
                />
            </Modal>
            <div className="layout-overlay layout-menu-toggle"></div>
            {LOGGED_USER?.profil?.organisme?.typeOrganisme !== "POOL" && (
                <div class="buy-now">
                    <button
                        onClick={() => navigate('/cession')}
                        class="btn btn-secondary btn-buy-now"
                        style={{
                            backgroundColor: '#242166',
                            borderColor: '#242166',
                            fontWeight: 500,
                            fontSize: '15px',
                            padding: '9px 24px',
                            borderRadius: '10px',
                            boxShadow: '0 6px 20px rgba(36, 33, 102, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <CheckOutlined style={{ fontSize: '18px' }} />
                        Effectuer une cession
                    </button>
                </div>
            )}
            <div className="drag-target"></div>
        </div>
    );
};

export default CessionsPool;
